import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismButton, PrismInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
// @ts-expect-error: Incorrect type
import preventDefault from 'ember-event-helpers/helpers/prevent-default';
import { not } from 'ember-truth-helpers';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import GeneralButtonsButtonSort from 'lumen/components/general/buttons/button-sort';
import HotelAndMarketRevenueInsightLoadingStateTitle from 'lumen/components/hotel-and-market/revenue-insight/loading-state-title';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import {
  HOTEL_SORT_BY,
  type HotelSortBy,
} from 'lumen/utils/constants/ri-constant-values';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketRevenueInsightHotelBlockHotelBlockTitleSignature {
  Args: {
    params?: {
      isLoading: boolean;
      searchValues?: {
        isSearchActive: boolean;
        searchValue: string;
      };
      segmentationName: string;
      updateIsSearchActive: (value: boolean) => void;
      updateSearchValue: (value: string) => void;
    };
    setSortHotelsAscending: (ascending: boolean) => void;
    setSortHotelsBy: (sortBy: HotelSortBy) => void;
    sortHotelsAscending: boolean;
    sortHotelsBy: HotelSortBy;
  };
}

// Remove the wrapping div so the child content is a direct child of the prism-card and can use the slots
export default class HotelAndMarketRevenueInsightHotelBlockHotelBlockTitle extends Component<HotelAndMarketRevenueInsightHotelBlockHotelBlockTitleSignature> {
  @service declare intl: Services['intl'];
  @service declare riSelections: Services['ri-selections'];

  @tracked tempSearchValue = '';

  /**
   * This updates the search value in the controller
   * We keep using the temp search value at all times and assume nothing went wrong updating the value
   * If something did go wrong, we'll send the correct value down to the controller next time
   */
  updateSearchValueTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      const { params } = this.args;

      this.tempSearchValue = (event.target as HTMLInputElement).value;
      await timeout();
      params?.updateSearchValue(this.tempSearchValue);
    },
  );

  get breakdownDetailRoute(): string {
    return `${this.riSelections.currentRoute}.breakdown-detail`;
  }

  get isSortedByChange(): boolean {
    return this.args.sortHotelsBy === HOTEL_SORT_BY.CHANGE;
  }

  get kpiString(): string {
    return this.intl.t(`revenue_insight.${this.riSelections.kpi}`);
  }

  get searchPlaceholder(): string {
    const segmentationName =
      this.riSelections.isCancellationsOnlyView &&
      this.segmentationName === RI_BREAKDOWNS.PICKUP
        ? 'cancellation_pickup'
        : this.segmentationName;

    return this.intl.t(`revenue_insight.search.${segmentationName}`);
  }

  get segmentationName(): string | undefined {
    const { params } = this.args;

    return params?.segmentationName;
  }

  get vsText(): string {
    if (this.segmentationName === 'pickup') {
      return this.riSelections.vsReferencePeriodText;
    }

    return this.riSelections.vsText;
  }

  @action enableSearch(enable: boolean): void {
    const { params } = this.args;

    params?.updateIsSearchActive(enable);
  }

  @action updateSort(sortBy: HotelSortBy): void {
    const {
      setSortHotelsAscending,
      setSortHotelsBy,
      sortHotelsAscending,
      sortHotelsBy,
    } = this.args;

    if (sortBy === sortHotelsBy) {
      // If the sortBy doesn't change: toggle the order
      setSortHotelsAscending(!sortHotelsAscending);
    } else {
      // If the sortBy does change: update it and set the order to descending by default
      setSortHotelsBy(sortBy);
      setSortHotelsAscending(false);
    }
  }

  <template>
    <div class="card-title-header card-header">
      <div
        class="title-box block-list-title-container flex items-center justify-between
          {{if @params.searchValues.isSearchActive 'title-box--search-active'}}"
      >
        {{#if @params.isLoading}}
          <HotelAndMarketRevenueInsightLoadingStateTitle />
        {{else if @params.searchValues.isSearchActive}}
          <PrismInput
            @autofocus={{true}}
            @iconAfter={{hash name="search"}}
            @onInput={{this.updateSearchValueTask.perform}}
            @placeholder={{this.searchPlaceholder}}
            @removePadding={{true}}
            @value={{this.tempSearchValue}}
            {{outsideClickHandler (fn this.enableSearch false)}}
          />
        {{else}}
          <div class="subscription-title-column"></div>
          <div class="sortable-column flex items-center justify-between">
            <button
              class="button--unstyled current-value
                {{if this.riSelections.isTargetActive 'current-value--target'}}
                flex items-center justify-between"
              type="button"
              {{on
                "click"
                (preventDefault (fn this.updateSort HOTEL_SORT_BY.KPI))
              }}
            >
              <span class="truncate">{{this.kpiString}}</span>
              <GeneralButtonsButtonSort
                @isAscending={{@sortHotelsAscending}}
                @isSorted={{not this.isSortedByChange}}
              />
            </button>
            <button
              class="button--unstyled reference-value
                {{if
                  this.riSelections.isTargetActive
                  'reference-value--target'
                }}
                reference-value--row"
              type="button"
              {{on
                "click"
                (preventDefault (fn this.updateSort HOTEL_SORT_BY.CHANGE))
              }}
            >
              <span class="truncate">{{this.vsText}}</span>
              <GeneralButtonsButtonSort
                @isAscending={{@sortHotelsAscending}}
                @isSorted={{this.isSortedByChange}}
              />
            </button>
          </div>
          <div class="icon-column ml-200 flex items-center justify-end gap-50">
            <PrismButton
              @buttonType="ghost"
              @icon="search"
              @onClick={{fn this.enableSearch true}}
              @size="small"
              data-event-id="hotel-revenue-insight-breakdown-search"
              data-event-value="subscription_id"
            />

            <LinkTo
              @query={{hash segmentationId="subscription_id"}}
              @route={{this.breakdownDetailRoute}}
              tabindex="-1"
            >
              <PrismButton
                @buttonType="ghost"
                @icon="expand"
                @size="small"
                data-event-id="hotel-revenue-insight-breakdown-expand"
                data-event-value="subscription_id"
              />
            </LinkTo>
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
